import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  addDays,
  isSameDay,
  isWithinInterval,
  parseISO,
  format,
  startOfDay,
} from 'date-fns';

// Runtime constants for CalendarEventType (avoids importing TS enum from shared workspace)
export const CalendarEventType = {
  Meeting: 'meeting' as const,
  Invoice: 'invoice' as const,
  Bill: 'bill' as const,
  Payment: 'payment' as const,
};

export type CalendarEventTypeKey = typeof CalendarEventType[keyof typeof CalendarEventType];

export interface CalendarEvent {
  id: string;
  ownerId: string;
  buildingId: string;
  title: string;
  date: string;
  endDate?: string;
  startTime?: string;
  endTime?: string;
  type: CalendarEventTypeKey;
  entityId: string;
  status?: string;
  amount?: number;
}

export interface Meeting {
  id: string;
  ownerId: string;
  buildingId: string;
  title: string;
  date: string;
  startTime?: string;
  endTime?: string;
  recordingStatus?: string;
  [key: string]: unknown;
}

export interface FinancialRecord {
  id: string;
  ownerId: string;
  buildingId: string;
  type: 'invoice' | 'bill' | 'payment';
  date: string;
  dueDate?: string;
  amount?: number;
  status?: string;
  description?: string;
  invoiceNumber?: string;
  supplierName?: string;
  contactName?: string;
  title?: string;
  unitName?: string;
  reference?: string;
  [key: string]: unknown;
}

export const EVENT_COLORS: Record<CalendarEventTypeKey, { bg: string; border: string; dot: string; text: string }> = {
  [CalendarEventType.Meeting]: { bg: 'bg-primary-50', border: 'border-blue-400', dot: 'bg-primary-500', text: 'text-primary-700' },
  [CalendarEventType.Invoice]: { bg: 'bg-amber-50', border: 'border-amber-400', dot: 'bg-amber-500', text: 'text-amber-700' },
  [CalendarEventType.Bill]: { bg: 'bg-red-50', border: 'border-red-400', dot: 'bg-red-500', text: 'text-red-700' },
  [CalendarEventType.Payment]: { bg: 'bg-green-50', border: 'border-green-400', dot: 'bg-green-500', text: 'text-green-700' },
};

export const EVENT_TYPE_LABELS: Record<CalendarEventTypeKey, string> = {
  [CalendarEventType.Meeting]: 'Meeting',
  [CalendarEventType.Invoice]: 'Invoice',
  [CalendarEventType.Bill]: 'Bill',
  [CalendarEventType.Payment]: 'Payment',
};

export function normalizeMeetings(meetings: Meeting[]): CalendarEvent[] {
  return meetings.map((m) => ({
    id: `meeting-${m.id}`,
    ownerId: m.ownerId,
    buildingId: m.buildingId,
    title: m.title,
    date: m.date,
    startTime: m.startTime,
    endTime: m.endTime,
    type: CalendarEventType.Meeting,
    entityId: m.id,
    status: m.recordingStatus,
  }));
}

export function normalizeFinancialRecords(records: FinancialRecord[]): CalendarEvent[] {
  return records.map((r) => {
    let title = r.description || '';
    let type: CalendarEventTypeKey = CalendarEventType.Bill;
    if (r.type === 'invoice') {
      type = CalendarEventType.Invoice;
      title = r.invoiceNumber ? `Invoice #${r.invoiceNumber} — ${r.supplierName || r.contactName || ''}` : r.description || 'Invoice';
    } else if (r.type === 'payment') {
      type = CalendarEventType.Payment;
      title = r.unitName ? `Payment — ${r.unitName}` : r.reference ? `Payment — ${r.reference}` : 'Service Charge';
    } else {
      // bill
      title = r.title || r.description || 'Bill';
    }
    return {
      id: `${r.type}-${r.id}`,
      ownerId: r.ownerId,
      buildingId: r.buildingId,
      title,
      date: r.dueDate || r.date,
      type,
      entityId: r.id,
      status: r.status,
      amount: r.amount,
    };
  });
}

export function normalizeToCalendarEvents(
  meetings: Meeting[],
  financialRecords: FinancialRecord[],
): CalendarEvent[] {
  return [
    ...normalizeMeetings(meetings),
    ...normalizeFinancialRecords(financialRecords),
  ];
}

export function getEventsForDateRange(events: CalendarEvent[], start: Date, end: Date): CalendarEvent[] {
  return events.filter((e) => {
    const d = parseISO(e.date);
    return isWithinInterval(d, { start: startOfDay(start), end: endOfWeek(end) });
  });
}

export function getUpcomingEvents(events: CalendarEvent[], weeks: number, referenceDate: Date = new Date()): CalendarEvent[] {
  const start = startOfDay(referenceDate);
  const end = addDays(start, weeks * 7);
  return events
    .filter((e) => {
      const d = parseISO(e.date);
      return isWithinInterval(d, { start, end });
    })
    .sort((a, b) => a.date.localeCompare(b.date));
}

export function groupEventsByDate(events: CalendarEvent[]): Map<string, CalendarEvent[]> {
  const grouped = new Map<string, CalendarEvent[]>();
  for (const event of events) {
    const key = event.date.substring(0, 10); // YYYY-MM-DD
    const list = grouped.get(key) || [];
    list.push(event);
    grouped.set(key, list);
  }
  return grouped;
}

export function getCalendarDays(year: number, month: number): Date[][] {
  const monthStart = startOfMonth(new Date(year, month, 1));
  const monthEnd = endOfMonth(monthStart);
  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });

  const allDays = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  // Pad to 6 rows if needed
  while (allDays.length < 42) {
    allDays.push(addDays(allDays[allDays.length - 1], 1));
  }

  const weeks: Date[][] = [];
  for (let i = 0; i < allDays.length; i += 7) {
    weeks.push(allDays.slice(i, i + 7));
  }
  return weeks;
}

export function getWeekDays(date: Date): Date[] {
  const weekStart = startOfWeek(date, { weekStartsOn: 1 });
  return eachDayOfInterval({ start: weekStart, end: addDays(weekStart, 6) });
}

export function getEventsForDay(events: CalendarEvent[], date: Date): CalendarEvent[] {
  return events.filter((e) => isSameDay(parseISO(e.date), date));
}

export function formatDateHeader(date: Date, referenceDate: Date = new Date()): string {
  if (isSameDay(date, referenceDate)) return 'Today';
  if (isSameDay(date, addDays(referenceDate, 1))) return 'Tomorrow';
  return format(date, 'EEE d MMM');
}

export function getEntityRoute(type: CalendarEventTypeKey, entityId: string): string {
  switch (type) {
    case CalendarEventType.Meeting:
      return `/dashboard/meetings/${entityId}`;
    case CalendarEventType.Invoice:
    case CalendarEventType.Bill:
    case CalendarEventType.Payment:
      return `/dashboard/financial-records/${entityId}`;
  }
}

export function countEventsByType(events: CalendarEvent[]): Record<CalendarEventTypeKey, number> {
  const counts = {
    [CalendarEventType.Meeting]: 0,
    [CalendarEventType.Invoice]: 0,
    [CalendarEventType.Bill]: 0,
    [CalendarEventType.Payment]: 0,
  };
  for (const event of events) {
    counts[event.type]++;
  }
  return counts;
}
